import React from 'react';
import { Box, Heading, Link, Text, VStack, VisuallyHidden } from '@chakra-ui/react';
import {
  ExternalLink,
  Book,
  FileText,
  Settings,
  Users,
  User,
  Calendar,
  AlertTriangle,
  Package,
  Shield,
  RefreshCw,
  PlayCircle,
  GitBranch,
  Tag,
  UserCheck,
} from 'lucide-react';
import { RESOURCES_LINKS } from '@/utils/constants';

interface ResourcesSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const getIconForLink = (title: string): React.ReactNode => {
  const iconMap: Record<string, React.ReactNode> = {
    // Teams and Users
    'Teams Documentation': <Users size={18} />,
    'Manage Users': <Users size={18} />,
    'User Profile': <User size={18} />,
    'User Roles': <Users size={18} />,
    'Advanced Permissions': <Shield size={18} />,
    // On-Call Schedules
    'Schedule Basics': <Calendar size={18} />,
    'Schedule Examples': <Calendar size={18} />,
    'My On-Call Shifts': <Calendar size={18} />,
    // Escalation Policies
    'Escalation Policy Basics': <AlertTriangle size={18} />,
    'Round Robin Scheduling': <RefreshCw size={18} />,
    'Escalation Policies & Schedules': <AlertTriangle size={18} />,
    // Services Graph
    'Services and Integrations': <Package size={18} />,
    'Configurable Service Settings': <Settings size={18} />,
    'Custom Fields on Services': <FileText size={18} />,
    'Service Ownership Ops': <Book size={18} />,
    'Service Graph Standards': <PlayCircle size={18} />,
    'Services & Integrations': <PlayCircle size={18} />,
    // Incident Management
    'Incident Workflows': <GitBranch size={18} />,
    'Incident Types': <Tag size={18} />,
    'Incident Roles': <UserCheck size={18} />,
    'Custom Fields on Incidents': <FileText size={18} />,
  };
  return iconMap[title] || <FileText size={18} />;
};

export const ResourcesSidebar: React.FC<ResourcesSidebarProps> = ({ isCollapsed, onToggle }) => {
  return (
    <Box
      as="aside"
      aria-label="Help and Resources"
      w={isCollapsed ? '40px' : '320px'}
      minW={0}
      h="100%"
      bg="white"
      borderLeft="1px solid"
      borderColor="gray.200"
      p={isCollapsed ? 2 : 6}
      overflowY="auto"
      overflowX="hidden"
      position="relative"
      transition="all 0.3s ease-in-out"
      display="block"
      flexShrink={0}
    >
      {!isCollapsed && (
        /* Expanded State */
        <>
          <Heading size="md" mb={4}>
            Help & Resources
          </Heading>

          <Text fontSize="sm" color="gray.600" mb={6}>
            Quick access to documentation, guides, and support resources.
          </Text>

          <VStack align="stretch" spacing={6}>
            {RESOURCES_LINKS.map((group) => (
              <Box key={group.title}>
                <Heading size="xs" mb={3} color="gray.700">
                  {group.title}
                </Heading>
                <VStack align="stretch" spacing={3}>
                  {group.items.map((item, index) => (
                    <React.Fragment key={item.title + '-' + index}>
                      <Link
                        href={item.link}
                        isExternal
                        textDecoration="none"
                        _hover={{ textDecoration: 'none' }}
                      >
                        <Box
                          px={3}
                          py={1}
                          borderRadius="md"
                          _hover={{ bg: 'gray.50' }}
                          transition="background 0.2s"
                        >
                          <Box display="flex" alignItems="center" gap={2}>
                            {getIconForLink(item.title)}
                            <Text fontSize="sm" color="gray.700">
                              {item.title}
                              <VisuallyHidden> (opens in new window)</VisuallyHidden>
                            </Text>
                            <ExternalLink size={14} style={{ marginLeft: 'auto' }} aria-hidden="true" />
                          </Box>
                        </Box>
                      </Link>
                    </React.Fragment>
                  ))}
                </VStack>
              </Box>
            ))}
          </VStack>
        </>
      )}

      {/* Toggle Button */}
      <Box
        position="absolute"
        top="50%"
        left={0}
        transform="translateY(-50%)"
        zIndex={10}
      >
        <Box
          as="button"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={onToggle}
          w="24px"
          h="60px"
          bg="white"
          borderRadius="0 8px 8px 0"
          cursor="pointer"
          display="flex"
          alignItems="center"
          justifyContent="center"
          _hover={{ bg: 'gray.50' }}
          _active={{ bg: 'gray.100' }}
          transition="all 0.2s"
        >
          <Box
            w="2px"
            h="24px"
            bg="gray.400"
            borderRadius="full"
          />
        </Box>
      </Box>
    </Box>
  );
};
